import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

const passwordRules =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;

// Defined at module level so React never remounts the input on state change
const PasswordField = ({ id, label, name, placeholder, show, onToggle, value, onChange, onBlur, touched, error }) => (
  <div className="form-group">
    <label htmlFor={id}>{label}</label>
    <div className="password-input">
      <input
        id={id}
        type={show ? "text" : "password"}
        name={name}
        className={`form-control ${touched && error ? "is-invalid" : ""}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
      />
      <span className="icon-btn cursor-pointer" onClick={onToggle}>
        <i className={show ? "icon-icon-2" : "icon-icon-1"}></i>
      </span>
    </div>
    {touched && error && (
      <div className="invalid-feedback d-block">{error}</div>
    )}
  </div>
);

PasswordField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  touched: PropTypes.bool,
  error: PropTypes.string,
};

PasswordField.defaultProps = {
  touched: false,
  error: "",
};

const ChangePassword = () => {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const headers = {
    "api-key": process.env.REACT_APP_API_KEY,
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  };

  const formik = useFormik({
    initialValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      current_password: Yup.string()
        .required("Current password is required")
        .min(8, "Must be at least 8 characters")
        .matches(passwordRules, "Must have uppercase, lowercase, number and special character"),
      new_password: Yup.string()
        .required("New password is required")
        .min(8, "Must be at least 8 characters")
        .matches(passwordRules, "Must have uppercase, lowercase, number and special character"),
      confirm_password: Yup.string()
        .required("Please confirm your new password")
        .oneOf([Yup.ref("new_password")], "Passwords do not match"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setError("");
      setSuccess("");
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}auth/change_password`,
          {
            current_password: values.current_password,
            new_password: values.new_password,
            confirm_password: values.confirm_password,
          },
          { headers }
        );
        if (res.data.status === "success") {
          setSuccess("Password changed successfully!");
          resetForm();
        } else {
          setError(res.data.message?.keyword || "Failed to change password.");
        }
      } catch (err) {
        setError(err.response?.data?.message?.keyword || "Something went wrong.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="admin-page">
      <div className="my-account-header">
        <h1 className="page-section-title">Change Password</h1>
        <p className="my-account-subtitle">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="my-account-form">
        <form onSubmit={formik.handleSubmit}>
          <PasswordField
            id="cp-current"
            name="current_password"
            label="Enter Current Password"
            placeholder="Enter current password"
            show={showCurrent}
            onToggle={() => setShowCurrent((v) => !v)}
            value={formik.values.current_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.current_password}
            error={formik.errors.current_password}
          />
          <PasswordField
            id="cp-new"
            name="new_password"
            label="Enter New password"
            placeholder="Enter new password"
            show={showNew}
            onToggle={() => setShowNew((v) => !v)}
            value={formik.values.new_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.new_password}
            error={formik.errors.new_password}
          />
          <PasswordField
            id="cp-confirm"
            name="confirm_password"
            label="Confirm Password"
            placeholder="Confirm password"
            show={showConfirm}
            onToggle={() => setShowConfirm((v) => !v)}
            value={formik.values.confirm_password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.confirm_password}
            error={formik.errors.confirm_password}
          />

          {error && <p className="text-danger mb-3">{error}</p>}
          {success && <p className="text-success mb-3">{success}</p>}

          <button
            type="submit"
            className="btns my-account-save-btn"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? "Updating..." : "Change Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
